import React, { useContext, useMemo } from "react";
import { ExpandedState, OnChangeFn } from "@tanstack/react-table";
import { ArAgingDetailResponse } from "@common/types/reports/types";
import { ViewTable } from "@design-system/ViewTable/ViewTable";
import { EmptyState } from "@design-system/EmptyState/EmptyState";
import { AccountingReportContext } from "../AccountingReport.context";
import { buildArAgingDetailRows } from "./arAgingDetailViewBuilder";
import {
    buildArAgingDetailConfig,
    buildArAgingDetailView,
} from "./arAgingDetailViewConfig";
import styles from "./ArAgingDetailTable.module.scss";

interface Props {
    data: ArAgingDetailResponse;
    expanded: ExpandedState;
    onExpandedChange: OnChangeFn<ExpandedState>;
    footer?: React.ReactNode;
}

export const ArAgingDetailTable: React.FC<Props> = ({
    data,
    expanded,
    onExpandedChange,
    footer,
}) => {
    const { numberFormat } = useContext(AccountingReportContext);
    const rows = useMemo(() => buildArAgingDetailRows(data), [data]);
    const view = useMemo(
        () =>
            buildArAgingDetailView({ showEntityColumn: data.showEntityColumn }),
        [data.showEntityColumn],
    );
    const config = useMemo(
        () =>
            buildArAgingDetailConfig({
                showEntityColumn: data.showEntityColumn,
                numberFormat,
            }),
        [data.showEntityColumn, numberFormat],
    );

    return (
        <ViewTable
            data={rows}
            view={view}
            config={config}
            className={styles.arAgingDetailTable}
            emptyView={() => (
                <EmptyState header="No open A/R for this selection" />
            )}
            rowBorders={false}
            enableColumnResizing
            fixedTopOffsetPx
            expanded={expanded}
            onExpandedChange={onExpandedChange}
            nestingIndentPx={16}
            footer={footer}
        />
    );
};
